import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Reviews Carousel CVA Configuration
 *
 * Compact design — small cards with product footer, sized like homepage product tiles.
 *
 * Design tokens from tailwind.config.js:
 * - bg-cards-bg      — card background
 * - text-text-color  — main text
 * - text-second-btn-bg — muted / secondary text
 * - font-main-font   — Poppins heading font
 */

/** Wrapper for the whole reviews carousel section */
export const reviewsCarouselSectionVariants = cva('homepage-reviews-carousel')
export type ReviewsCarouselSectionVariants = VariantProps<typeof reviewsCarouselSectionVariants>

/** Summary row above the carousel: average rating + stars (no count) */
export const reviewsCarouselSummaryVariants = cva(
  'flex items-center justify-center gap-2 mb-3'
)
export type ReviewsCarouselSummaryVariants = VariantProps<typeof reviewsCarouselSummaryVariants>

/** Average rating number in the summary row */
export const reviewsCarouselAverageVariants = cva(
  'text-xl font-bold text-text-color font-main-font leading-none'
)
export type ReviewsCarouselAverageVariants = VariantProps<typeof reviewsCarouselAverageVariants>

/** Swiper container */
export const reviewsCarouselSwiperVariants = cva('reviews-carousel-swiper py-3')
export type ReviewsCarouselSwiperVariants = VariantProps<typeof reviewsCarouselSwiperVariants>

/** Individual slide */
export const reviewsCarouselSlideVariants = cva('!h-auto bg-cards-bg px-1 py-2')
export type ReviewsCarouselSlideVariants = VariantProps<typeof reviewsCarouselSlideVariants>

/**
 * Review card container — compact, with the reviewed product kept in the footer.
 * Consistent height via flex-col + h-full so all cards in a row match.
 */
export const reviewCardVariants = cva(
  'group relative flex h-full min-h-[155px] w-full flex-col overflow-hidden border-2 border-white bg-white p-2 transition-colors duration-300 hover:border-text-color xl:min-h-[175px]'
)
export type ReviewCardVariants = VariantProps<typeof reviewCardVariants>

/** Reviewer nickname */
export const reviewCardNicknameVariants = cva(
  'react-css font-main-font text-[11px] font-semibold leading-tight text-text-color xl:text-xs'
)
export type ReviewCardNicknameVariants = VariantProps<typeof reviewCardNicknameVariants>

/** Review title / summary (optional) */
export const reviewCardSummaryVariants = cva(
  'react-css font-main-font text-[11px] font-semibold leading-tight text-text-color line-clamp-1 xl:text-xs'
)
export type ReviewCardSummaryVariants = VariantProps<typeof reviewCardSummaryVariants>

/** Review body text */
export const reviewCardBodyVariants = cva(
  'react-css flex-1 font-main-font text-[11px] leading-snug text-second-btn-bg line-clamp-2'
)
export type ReviewCardBodyVariants = VariantProps<typeof reviewCardBodyVariants>

/** Formatted date */
export const reviewCardDateVariants = cva('react-css font-main-font text-[10px] text-second-btn-bg')
export type ReviewCardDateVariants = VariantProps<typeof reviewCardDateVariants>

/** Product thumbnail image */
export const reviewCardProductImageVariants = cva(
  'h-9 w-9 shrink-0 object-contain text-[9px] leading-tight transition-transform duration-500 group-hover:scale-105'
)
export type ReviewCardProductImageVariants = VariantProps<typeof reviewCardProductImageVariants>

/** Product name text in the card footer */
export const reviewCardProductNameVariants = cva(
  'react-css flex-1 font-main-font text-[11px] font-medium leading-tight text-text-color line-clamp-2 xl:text-xs'
)
export type ReviewCardProductNameVariants = VariantProps<typeof reviewCardProductNameVariants>

/**
 * Skeleton grid — mirrors the carousel breakpoints (2 / 3 / 5).
 */
export const reviewsCarouselSkeletonGridVariants = cva(
  'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 py-3'
)
export type ReviewsCarouselSkeletonGridVariants = VariantProps<typeof reviewsCarouselSkeletonGridVariants>

/** Single skeleton card */
export const reviewsCarouselSkeletonItemVariants = cva('h-[155px] border-2 border-white xl:h-[175px]')
export type ReviewsCarouselSkeletonItemVariants = VariantProps<typeof reviewsCarouselSkeletonItemVariants>
